import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, List, UserCheck, UserX, Users, Calendar, Activity, TrendingUp, UserPlus } from 'lucide-react';
import { useAdminEmail } from './AdminEmailContext';

const SCHEDULED_JOBS = [
  { key: 'coming_episodes', label: 'Coming Episodes Digest', prefField: 'weekly_digest', icon: Calendar },
  { key: 'social_activity', label: 'Social Activity Digest', prefField: 'social_notifications', icon: Users },
  { key: 'grazing_recommendations', label: 'Grazing Recommendations', prefField: 'grazing_recommendations', icon: TrendingUp },
  { key: 'onboarding', label: 'Onboarding Emails', prefField: 'feature_announcements + signup window', icon: UserPlus },
  { key: 'heartbeat', label: 'System Heartbeat', prefField: 'dev only', icon: Activity },
];

const RecipientsTab = () => {
  const ctx = useAdminEmail();
  const {
    emailStats, loadManualRecipients, loadingManualRecipients,
    loadScheduledRecipients, loadingScheduledRecipients,
  } = ctx;
  const [view, setView] = useState('manual'); // 'manual' | 'scheduled'

  return (
    <div className="recipients-tab">
      <Card className="recipients-card">
        <CardHeader>
          <CardTitle>Recipients</CardTitle>
          <CardDescription>
            Manual announcements use the <code>feature_announcements</code> preference. Each automated digest filters on its own preference field — switch tabs below to see exactly who will receive each one.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="recipient-view-tabs">
            <button
              className={`recipient-view-tab ${view === 'manual' ? 'active' : ''}`}
              onClick={() => setView('manual')}
              data-testid="recipient-view-manual"
            >
              <Users size={16} /> Manual Announcements
            </button>
            <button
              className={`recipient-view-tab ${view === 'scheduled' ? 'active' : ''}`}
              onClick={() => setView('scheduled')}
              data-testid="recipient-view-scheduled"
            >
              <Calendar size={16} /> Scheduled Emails
            </button>
          </div>

          {view === 'manual' && (
            <>
              <div className="recipient-summary">
                <div className="summary-item primary">
                  <div className="summary-number">{emailStats?.recipients?.total_active || 0}</div>
                  <div className="summary-label">Active Recipients</div>
                  <div className="summary-desc">Will receive your manual announcements</div>
                </div>
                <div className="summary-item flagged">
                  <div className="summary-number">{emailStats?.recipients?.flagged || 0}</div>
                  <div className="summary-label">Flagged / Test Users</div>
                  <div className="summary-desc">Excluded from mailings</div>
                </div>
              </div>

              <div className="view-recipients-action">
                <Button
                  onClick={loadManualRecipients}
                  disabled={loadingManualRecipients}
                  className="view-recipients-btn-large"
                  data-testid="view-recipients-btn"
                >
                  {loadingManualRecipients ? (
                    <><Loader2 size={18} className="spinning" /> Loading Recipients...</>
                  ) : (
                    <><List size={18} /> View &amp; Manage Recipients</>
                  )}
                </Button>
                <p className="action-hint">Filters on <code>email_preferences.feature_announcements</code>.</p>
              </div>

              <div className="recipient-breakdown">
                <h3>Manual Announcement Breakdown</h3>
                <div className="breakdown-grid">
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <UserCheck size={20} />
                      <span>Explicitly Opted In</span>
                    </div>
                    <div className="breakdown-value">{emailStats?.recipients?.opted_in || 0}</div>
                    <div className="breakdown-desc">Users who actively enabled email notifications</div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <Users size={20} />
                      <span>Default (No Preference)</span>
                    </div>
                    <div className="breakdown-value">{emailStats?.recipients?.default_opted_in || 0}</div>
                    <div className="breakdown-desc">Users who haven't set preferences yet</div>
                  </div>

                  <div className="breakdown-item muted">
                    <div className="breakdown-header">
                      <UserX size={20} />
                      <span>Opted Out</span>
                    </div>
                    <div className="breakdown-value">{emailStats?.recipients?.opted_out || 0}</div>
                    <div className="breakdown-desc">Users who unsubscribed from emails</div>
                  </div>

                  <div className="breakdown-item flagged">
                    <div className="breakdown-header">
                      <UserX size={20} />
                      <span>Flagged as Test</span>
                    </div>
                    <div className="breakdown-value">
                      {emailStats?.recipients?.manually_flagged || 0} manual, {emailStats?.recipients?.auto_detected || 0} auto
                    </div>
                    <div className="breakdown-desc">Test accounts excluded from mass emails</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {view === 'scheduled' && (
            <div className="scheduled-recipients-grid">
              <p className="scheduled-recipients-intro">
                Each row shows the audience for one scheduled email. Click <strong>View Recipients</strong> to see the actual users (and flagged ones) for that specific job.
              </p>
              {SCHEDULED_JOBS.map(job => {
                const Icon = job.icon;
                return (
                  <div key={job.key} className="scheduled-recipient-row" data-testid={`scheduled-recipient-row-${job.key}`}>
                    <div className="scheduled-recipient-info">
                      <Icon size={20} />
                      <div>
                        <div className="scheduled-job-label">{job.label}</div>
                        <div className="scheduled-job-pref">
                          Filters on: <code>{job.prefField}</code>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => loadScheduledRecipients(job.key)}
                      disabled={loadingScheduledRecipients === job.key}
                      className="scheduled-view-btn"
                      data-testid={`scheduled-view-${job.key}`}
                    >
                      {loadingScheduledRecipients === job.key ? (
                        <><Loader2 size={16} className="spinning" /> Loading...</>
                      ) : (
                        <><List size={16} /> View Recipients</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RecipientsTab;
